import React, { useEffect, useState, useRef, useCallback } from "react";
import { Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ProgressBar from "../ProgressBar/ProgressBar";
import ErrorPricingModal from "../Modal/ErrorPricingModal/ErrorPricingModal";
import {
  BrandDetails,
  FormValues,
  FranchiseDetail,
  TechAvailabilityResponse,
} from "../../../../Interface/scheduler/form";
import {
  validateFields,
  validateMapAreaSelectionFields,
  validatePricingFields,
  validateServiceSelectionFields,
} from "../../../../common/components/FieldValidator/validateFields";
import {
  fetchFranchiseLookupByServiceAddress,
  fetchGeoLocation,
  ContactUsApiCallHandler,
  WebLocationsMilesEndpoint,
  fetchFranchisePosLookup,
  fetchAddressSuggestions,
} from "../../../../service/apiService";
import Loader from "../../../../common/components/Loader/Loader";
import SkeletonLoader from "../../../../common/components/SkeletonLoader/SkeletonLoader";
import { useFormContext } from "../../../../common/contexts/FormContext";
import { useFormConfigContext } from "../../../../common/contexts/FormConfigContext";
import { useFranchiseLookup } from "../../hooks/useFranchiseLookup";
import usePostQuote from "../../../../common/hooks/usePostQuote";
const AddressBumper = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/AddressLocation" */ "../AddressBumper/AddressBumper"
    ),
);
import { getTopSuggestedAddress } from "../../../../common/components/FormBuilder/AddressDropdown";
const ErrorModal = React.lazy(
  () =>
    import(/* webpackChunkName: "scheduler/Errors" */ "../Modal/ErrorModal"),
);
const TechnicalErrorModal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/Errors" */ "../TechnicalErrorModal/TechnicalErrorModal"
    ),
);

const ZipcodeChangeModal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/Errors" */ "../ZipcodeChangeModal/ZipcodeChangeModal"
    ),
);

import {
  extractDomain,
  formatSelectedDate,
  getSessionAndCorrelationID,
  handleHMSMultipleLocationPOS,
  handleISNRedirect,
  handleOfferDetails,
  skipToNextStep,
} from "../../../../common/components/Helper/Helper";
const LocationsModal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/AddressLocation" */ "../LocationsModal/LocationsModal"
    ),
);

const AddressBumperFormModal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/AddressBumperFormModal" */ "../../../addressBumperModal/AddressBumperFormModal"
    ),
);
import {
  filterResults,
  findUnits,
  convertRange,
  getAllLocations,
  milesToKilometers,
  getBoundingBox,
} from "../Helper/Location";
import { OffersPage } from "../OffersPage/OffersPage";
import { PresignedUrlUploader } from "../../../../common/components/UploadFields/PresignedUrlUploader";
import {
  SKELETON_LOADER_LAYOUT_PAGE,
  AppConstants,
  conceptIDs,
  MULTIPLE_LOCATIONS_ERROR_MSG,
  SESSION_QUOTE_ESTIMATE,
  SESSION_NON_PRICEABLE_QUOTE_ESTIMATE,
  SESSION_TEXT_TILES,
  sharedStateKeys,
  stepTypes,
  noAddressMsg,
  SUGGEST_ADDRESS_VALUE,
  SESSION_UPSELL,
  IS_CONTACT_ME_BUTTON,
  PROTYPE_COMMERCIAL_TEXT,
  CustomerRecordUpdatedText,
  CustomerSuccessfullyAddedText,
  enabledConcepts,
  SESSION_SERVICE_API_ERROR,
  serviceDetailsLabels,
  conceptCodes,
  PROPERTY_AREA_CONFIRMATION,
  SESSION_CROSS_SELL,
} from "../../../../constants/constant";
import { BookingApiCallHandler } from "../Helper/BookingApiCallHandler";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";
import SchedulerStepHandler from "./SchedulerStepHandler";
import {
  checkGetCachedWrapperFunc,
  deepEqual,
  findPropertyById,
  landingPageOfferSourceValidation,
  retryWithExponentialBackoff,
  sessionStorageUtils,
  skipMLYCalendar,
} from "../../../../utility/utility";
import FormActionButtons from "../FormActionButtons/FormActionButtons";
import "./SchedulerSteps.css";
import StepTitle from "../StepTitle/StepTitle";
import TextIconTiles from "../../../../common/components/TextIconTiles/TextIconTiles";
import "../../../../utility/globalWindow"; // Import the globalWindow.ts file
import useNonServicableZipcodes from "../../hooks/useNonServicableZipcodes";
import useFilterData from "../../hooks/useFilterData";
import {
  addSVGIcon,
  getStepsToRemove,
  handleContactUsSingleStep,
  maybeAddWdgNonPriciableSkuId,
  pauseLeadTriggerBookingAPI,
  validateAllSteps,
  handleOfferValidation,
  canMoveToNextStep,
  attachOffer,
  handleOfferRemoval,
  getBannerPosition,
  isModifierFlowCalendarRequired,
  loadBrandDetails,
} from "./SchedulerStepHelper";
import { isBusinessNameHidden } from "../../../../common/hooks/useBusinessNameVisibility";
import { useFeeInspection } from "../../hooks/useFeeInspection";
import apiConfig from "../../../../service/apiConfig";
import { useJukPaymentScenario } from "../../hooks/useJukPaymentScenario";
import { usePaymentHandler } from "../../hooks/usePaymentHandler";
import { useScheduleTech } from "../../hooks/useScheduleTech";
import { useCurrentStep } from "../../../../common/hooks/useCurrentStep";
import OfferDetailsFooter from "../OffersPage/OfferDetailsFooter";
import useIsMobile from "../../../../common/hooks/useIsMobile";
import { useShelfStorageConfig } from "../../hooks/useShelfStorageConfig";
import { useUpdateSessionStorage } from "../../../../common/hooks/useUpdateSessionStorage";
import { useCheckAndValidateCouponFromUrl } from "../../hooks/useCouponCheck";
import OffersApply from "../../../../common/components/FormBuilder/OffersApply";
import { useHandleRedirectionFromShortForm } from "../../../../common/hooks/useHandleRedirectionFromShortForm";
import { useUserDetailsFromUrl } from "../../hooks/useUserDetailsFromUrl";
import { useUnifiedSync } from "../../hooks/useUnifiedSync";
import isInboundFromNbly, {
  clearSessionOffer,
} from "../../../../utility/checkInBoundFromNbly";
const SchedulerSteps = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { setValue, removeValue, state } = useSharedStateWithActions();
  const { currentStep, setCurrentStep } = useCurrentStep();
  const { isUnifiedSyncReady } = useUnifiedSync({
    maxWaitTime: 10000,
    maxInterval: 2000,
    initialInterval: 100,
    functionName: "GetCachedUnifiedSync",
  });
  const {
    formValues,
    setFormValues,
    isGlobalSkeletonLoading,
    setIsGlobalSkeletonLoading,
  } = useFormContext();
  const prevFormDataRef = useRef(formValues);
  const { formConfig } = useFormConfigContext();
  const { postQuote } = usePostQuote();

  const currentStepConfig = formConfig?.steps[currentStep];
  const { updatedStepConfig } = useShelfStorageConfig({ currentStepConfig });
  const checkAndValidateCoupon = useCheckAndValidateCouponFromUrl();

  if (currentStepConfig?.steptype === AppConstants.CONTACT_US_TYPE) {
    const showOptin = currentStepConfig?.showOptin?.toString();
    sessionStorage.setItem("showOptIn", showOptin);
  }

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const nonPricablePressureWashingDataRef = useRef([]);
  const nonPricableQuoteObjRef = useRef([]);
  const [totalUpdatedSteps, setTotalUpdatedSteps] = useState(
    formConfig ? formConfig.steps.length : 0,
  );
  const [isLoading, setIsLoading] = useState(false);
  const isHandlingNext = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [makeAnotherCall, setMakeAnotherCall] = useState(false);
  const [moveToNextPage, setMoveToNextPage] = useState(true);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [technicalModalOpen, setTechnicalModalOpen] = useState(false);
  const [isZipcodeChangeModalOpen, setIsZipcodeChangeModalOpen] =
    useState(false);
  const [scheduleCollection, setScheduleCollection] = useState<
    TechAvailabilityResponse[]
  >([]);
  const [online, setOnline] = useState(false);
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState(false);
  const [units, setunits] = useState("");
  const [franchiseLimit, setFranchiseLimit] = useState(50);
  const [foundNearByLocations, setFoundNearByLocations] = useState(false);
  const [noOfNearByLocations, setnoOfNearByLocations] = useState(0);
  const [allNearByLocations, setAllNearByLocations] = useState(null);
  const [brandDetails, setBrandDetails] = useState<BrandDetails>({});
  const [clickedFranchiseId, setClickedFranchiseId] = useState(null);
  const [clickedZipcode, setClickedZipcode] = useState<string | null>(null);
  const [oneLocationClicked, setOneLocationClicked] = useState(false);
  const [addressBumperOpen, setAddressBumperOpen] = useState(false);
  const [iframeLoadedWithoutErrors, setIframeLoadedWithoutErrors] =
    useState(false);
  const [hasMovedPastFirstStep, setHasMovedPastFirstStep] = useState(false);
  const [selectedOffer, setSelectedOffer] =
    useUpdateSessionStorage("offerDetails");
  const [localisedLogoSelect, setLocalisedLogoSelect] = useState(null);
  const [offerRemoved, setOfferRemoved] = useState(false);
  const [enteredAddress, setEnteredAddress] = useState(
    `${formValues?.address ?? ""} ${formValues?.address2 ?? ""} ${formValues?.zipcode ?? ""}`.trim(),
  );
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [calendarTimeSlot, setCalendarTimeSlot] = useState({
    startTime: null,
    endTime: null,
  });
  const [initialDate, setInitialDate] = useState("");
  const [enableCallBack, setEnableCallBack] = useState(false);
  const [notServicable, setNotServicable] = useState(false);
  const validateCESOffer =
    brandDetails.display_special_offers_from_CES as boolean;
  const alternateConcept = sessionStorage.getItem("alternateConcept");
  const enteredAddressWithoutZipcode = formValues?.address;
  const suggestedAddress = getTopSuggestedAddress();
  const zipRegex = /\b\d{4,5}\b/g;
  const foundZipCodes = suggestedAddress?.match(zipRegex) || [];
  const hasNoZipCodes = foundZipCodes.length === 0;
  const suggestedSelected =
    sessionStorage.getItem("selectedAddressFromDropdown") ?? "false";

  const { isShortFormDataSet, setIsShortFormDataSet } =
    useHandleRedirectionFromShortForm({
      formConfig,
      currentStep,
      setSelectedNearByLocationData,
    });
  const storedZipcode = sessionStorage.getItem("brandZipCode");
  const isNeighborlyFlow =
    formConfig?.conceptCode === conceptCodes.HMS_CONCEPT_CODE;
  const storedWebLocationId = localStorage.getItem("weblocationId");

  const loadBrandDetailsWrapper = async () => {
    await loadBrandDetails({
      conceptId: formConfig?.conceptid,
      setBrandDetails,
      setValue,
      setFranchiseLimit,
      setUnits: setunits,
      findUnits,
      setFormValues,
    });
  };

  useEffect(() => {
    skipToNextStep({
      storedZipcode,
      setFormValues,
      currentStepConfig,
      isNeighborlyFlow,
      currentStep,
      formConfig,
      setCurrentStep,
      setIsLoading,
      setOnline,
      storedWebLocationId,
    });
  }, [isUnifiedSyncReady]);
  useEffect(() => {
    if (formConfig && brandDetails && !offerRemoved) {
      formValues.conceptID = formConfig.conceptid;
      const loadOffer = async () => {
        const offer = await attachOffer(
          formConfig,
          formValues,
          validateCESOffer,
        );
        setSelectedOffer(offer);
      };

      loadOffer();
    }
  }, [formConfig, brandDetails, offerRemoved]);

  const [makePauseLeadBookingCall, setMakePauseLeadBookingCall] =
    useState(true);
  const [errorPricingModalOpen, setErrorPricingModalOpen] = useState(false);
  const { handleNonServicableZipcodes } = useNonServicableZipcodes({
    brandDetails,
    formValues,
    setFranchiseLimit,
    setIsModalOpen,
    setIsServiceModalOpen,
    foundNearByLocations,
    formConfig,
    searchNearbyLocations,
    ContactUsApiCallHandler,
    getSessionAndCorrelationID,
    state,
  });
  const { handleFeeInspection } = useFeeInspection();
  const { filterData } = useFilterData(formValues);

  const [
    oneClickedFranchiseWebLocationId,
    setOneClickedFranchiseWebLocationId,
  ] = useState(null);

  const handleJukPaymentScenario = useJukPaymentScenario();
  const { initiatePayment } = usePaymentHandler({
    onSuccess: () => {
      const redirectionUrl = formConfig?.redirectionPageUrl || "/confirmation";
      globalThis.location.replace(
        isInboundFromNbly()
          ? `${redirectionUrl}?leadconcept=${formConfig.conceptCode}`
          : redirectionUrl,
      );
    },
    onError: (message) => {
      console.error("Payment failed:", message.message);
    },
  });

  const { scheduleTech } = useScheduleTech({
    brandDetails,
    setIsLoading,
    setScheduleCollection,
    setOnline,
    setTechnicalModalOpen,
  });

  useUserDetailsFromUrl();

  useEffect(() => {
    if (alternateConcept && alternateConcept !== "null") {
      const localisedLogoObject = sessionStorage.getItem("headerLogoURL");
      const localisedLogoParsedObject = localisedLogoObject
        ? JSON.parse(localisedLogoObject)
        : null;
      const localisedLogo =
        localisedLogoParsedObject?.hamburgerViewLogoImage ||
        localisedLogoParsedObject?.logoImage;
      setLocalisedLogoSelect(localisedLogo);
      setValue(sharedStateKeys.LOCALISED_LOGO, localisedLogo);
    } else {
      setLocalisedLogoSelect(null);
      removeValue(sharedStateKeys.LOCALISED_LOGO);
      sessionStorage.removeItem("alternateConcept");
    }
  }, [alternateConcept, formValues?.zipcode]);

  const extractHref = (str) => {
    if (!str) return null;
    const match = str.match(/xlink:href="([^"]+)"/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const iconElement = extractHref(brandDetails?.icon_for_error_message);
    if (iconElement === null) {
      return;
    }
    addSVGIcon(errors, iconElement);
  }, [errors, brandDetails]);

  const { handleFranchiseLookup } = useFranchiseLookup();
  const nextButtonClickedEnable = brandDetails.pause_lead_flag;
  const bookingWrapperEnabled = brandDetails.capture_lead_flag as boolean;
  const upSellEnabled = (brandDetails.enable_upsell ||
    brandDetails.enable_crosssell ||
    brandDetails.both_upsell_and_cross_sell) as boolean;

  useEffect(() => {
    const errorElements = document.querySelectorAll(".error-message");
    if (errorElements.length > 0) {
      const firstErrorElement = errorElements[0] as HTMLElement;
      firstErrorElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors]);

  useEffect(() => {
    if (
      state[sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION] &&
      Object.keys(brandDetails).length > 0
    ) {
      searchNearbyLocations(
        formValues?.zipcode as string,
        brandDetails,
        state[sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION],
      );
    }
  }, [state[sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION]]);

  useEffect(() => {
    let localIframeLoadedWithoutErrors = iframeLoadedWithoutErrors;
    let targetOrigin: string | undefined;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;
      if (
        typeof event.data === "object" &&
        event.data?.status === "ready" &&
        event.data?.message === "Iframe loaded"
      ) {
        setIframeLoadedWithoutErrors(() => true);
        localIframeLoadedWithoutErrors = true;
      }
    };

    if (formConfig?.steps[currentStep]?.steptype === stepTypes.PAYMENT_IFRAME) {
      if (formConfig?.steps[currentStep]?.paymentUrl) {
        targetOrigin = extractDomain(
          formConfig?.steps[currentStep]?.paymentUrl,
        );
        window.addEventListener("message", handleMessage);
        setTimeout(() => {
          if (!localIframeLoadedWithoutErrors) {
            handleSubmit();
            console.warn("Iframe load timeout - no message received");
          }
        }, 5000);
      }
      if (formConfig?.steps[currentStep]?.paymentAPI) {
        setIframeLoadedWithoutErrors(() => true);
        localIframeLoadedWithoutErrors = true;
      }
    }

    if (
      formConfig?.steps[currentStep]?.steptype ===
        stepTypes.STEP_PRICE_QUOTE_REQUEST &&
      !state[sharedStateKeys.IS_PRICE_QUOTE_PAGE]
    ) {
      setValue(sharedStateKeys.IS_PRICE_QUOTE_PAGE, true);
    }

    if (
      formConfig?.steps[currentStep]?.steptype ===
        AppConstants.BOOK_AN_INSPECTION &&
      !state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]
    ) {
      setValue(sharedStateKeys.BOOK_AN_INSPECTION_ENABLE, true);
    }

    //In the first step we are clearing pricing information from session
    if (currentStep === 0) {
      sessionStorageUtils.removeItem(SESSION_NON_PRICEABLE_QUOTE_ESTIMATE);
      sessionStorageUtils.removeItem(SESSION_QUOTE_ESTIMATE);
      sessionStorageUtils.removeItem(SUGGEST_ADDRESS_VALUE);
      sessionStorageUtils.removeItem(SESSION_UPSELL);
      sessionStorageUtils.removeItem(SESSION_SERVICE_API_ERROR);
      sessionStorageUtils.removeItem(SESSION_CROSS_SELL);
      sessionStorageUtils.removeItem("contactUsTriggered");
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [formConfig, currentStep]);

  const handleLocationClick = async (
    zipcode: string,
    locationClicked: boolean,
    clickedFranchiseWebLocationId: string,
    franchiseDetail: FranchiseDetail,
  ) => {
    setIsLoading(true);
    setClickedZipcode(zipcode);
    setOneClickedFranchiseWebLocationId(clickedFranchiseWebLocationId);
    setOneLocationClicked(locationClicked);
    if (formConfig?.conceptCode === conceptCodes.HMS_CONCEPT_CODE) {
      await handleHMSMultipleLocationPOS(
        clickedFranchiseWebLocationId,
        setCurrentStep,
        setIsLoading,
        formConfig?.conceptCode,
      );
      setIsLocationsModalOpen(false);
      return;
    }
    if (formConfig.feeDetails?.displayInspectionFee)
      handleFeeInspection(clickedFranchiseWebLocationId);
    setClickedFranchiseId(franchiseDetail?.franchiseId);
    if (typeof window?.localizationHelper === "function") {
      window?.localizationHelper([franchiseDetail], true);
    }

    setIsLoading(false);
    setIsLocationsModalOpen(false);
  };

  useEffect(() => {
    if (hasMovedPastFirstStep && formValues?.zipcode) {
      const addressQuery = `${formValues?.address ?? ""} ${formValues.zipcode}`;
      fetchAddressSuggestions(
        "address",
        addressQuery,
        formValues.zipcode,
        setFormValues,
        () => {},
      );
    }
  }, [formValues?.zipcode, hasMovedPastFirstStep]);

  //This is for pauseLead booking api call.
  // We make booking API call only if the form data has changed
  useEffect(() => {
    const isEqual = deepEqual(prevFormDataRef.current, formValues);
    // Update only if the values are different
    if (!isEqual) {
      prevFormDataRef.current = formValues;
      setMakePauseLeadBookingCall(true);
    } else {
      setMakePauseLeadBookingCall((prev) => (prev ? false : prev));
    }
  }, [formValues]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseLocationsModal = () => {
    removeValue(sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION);
    setIsLocationsModalOpen(false);
  };

  const handleCloseServiceModal = () => {
    removeValue(sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION);
    setIsServiceModalOpen(false);
  };

  const handleCloseZipcodeChangeModal = () => {
    setIsZipcodeChangeModalOpen(false);
  };

  const handleCloseTechincalModal = () => {
    setTechnicalModalOpen(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    checkAndValidateCoupon();
    if (isInboundFromNbly()) clearSessionOffer();
    if (currentStep === 0)
      sessionStorage.setItem("landing-page-url", window.location.href);
  }, []);

  useEffect(() => {
    if (oneLocationClicked === true) {
      setFormValues((prevValues) => {
        const updatedValues = {
          ...prevValues,
          franchiseId: clickedFranchiseId,
          franchiseWebLocationId: oneClickedFranchiseWebLocationId,
          os_flow_50Mile_note: brandDetails.os_flow_50Mile_note,
        };
        if (state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]) {
          if (formConfig?.conceptid === conceptIDs.MRA_CONCEPT_ID) {
            setValue(sharedStateKeys.IS_WARRANTY, true);
          }
          setCurrentStep((prevStep) => prevStep + 1);
        } else {
          handleNext();
          setOneLocationClicked(false);
        }
        if (
          formConfig?.conceptid === conceptIDs.MRA_CONCEPT_ID &&
          formConfig?.steps[currentStep]?.steptype ===
            AppConstants.CONTACT_US_TYPE
        ) {
          setCurrentStep((prevStep) => prevStep - 1);
          BookingApiCallHandler(
            formConfig,
            updatedValues,
            setTechnicalModalOpen,
          );
        }
        return updatedValues;
      });
    }
  }, [clickedZipcode, oneLocationClicked]);

  useEffect(() => {
    if (formConfig?.conceptid) {
      loadBrandDetailsWrapper();
    }
    const loadScript = (src: string, id: string) => {
      if (!document.getElementById(id)) {
        const script = document.createElement("script");
        script.src = src;
        script.id = id;
        script.async = false;
        document.head.appendChild(script);
      }
    };
    loadScript(
      "https://js.api.here.com/v3/3.1/mapsjs-core.js",
      "mapsjs-core-js",
    );
    loadScript("https://js.api.here.com/v3/3.1/mapsjs-ui.js", "mapsjs-ui-js");
  }, [formConfig?.conceptid]);

  const handleShortFormRedirection = useCallback(() => {
    if (!isShortFormDataSet || Object.keys(brandDetails)?.length === 0) {
      return;
    }
    setIsGlobalSkeletonLoading(true);
    const executeHandleNext = () => {
      handleNext().finally(() => {
        setTimeout(() => {
          setIsGlobalSkeletonLoading(false);
        }, 450);
        sessionStorageUtils.removeItem("shouldRedirectToLeadFlow");
        sessionStorageUtils.removeItem("isNearbyRedirect");
      });
      setIsShortFormDataSet(false);
    };

    // Use the retry utility function
    const cleanup = retryWithExponentialBackoff(
      checkGetCachedWrapperFunc,
      executeHandleNext,
    );

    return cleanup;
  }, [isShortFormDataSet, brandDetails]);

  useEffect(() => {
    setEnteredAddress(
      `${formValues?.address ?? ""} ${formValues?.address2 ?? ""} ${formValues?.zipcode ?? ""}`.trim(),
    );
    handleShortFormRedirection();
  }, [formValues, brandDetails]);

  useEffect(() => {
    if (
      formConfig?.steps[currentStep]?.steptype === "request-time-section" &&
      (selectedScheduleId?.length !== 0 || enableCallBack)
    ) {
      setMoveToNextPage(true);
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      scheduleId: selectedScheduleId,
      date:
        selectedDate?.length > 0
          ? selectedDate
          : formatSelectedDate(initialDate),
      slot: selectedSlot,
      startTime: calendarTimeSlot.startTime,
      endTime: calendarTimeSlot.endTime,
    }));
    if (enableCallBack) {
      setFormValues((prevValues) => ({
        ...prevValues,
        scheduleId: "",
        technicianIds: "",
      }));
    }
  }, [selectedScheduleId, selectedDate, enableCallBack]);

  useEffect(() => {
    setTotalUpdatedSteps(formConfig ? formConfig?.steps?.length : 0);
  }, [formConfig?.steps?.length]);

  useEffect(() => {
    const fullAttrOnLoad = localStorage.getItem("fullAttrOnLoad");
    const conceptCode = sessionStorage.getItem("conceptCode");
    if (fullAttrOnLoad && conceptCode) {
      try {
        const parsedFullAttrOnLoad = JSON.parse(fullAttrOnLoad);
        if (parsedFullAttrOnLoad?.brandCode !== conceptCode) {
          localStorage.removeItem("doingBusinessAs");
          localStorage.removeItem("localTagLine");
          localStorage.removeItem("weblocationId");
        }
      } catch (error) {
        console.error("Error parsing fullAttrOnLoad:", error);
      }
    }
  }, [formConfig?.conceptCode]);

  function setSelectedNearByLocationData(
    franchiseId: number,
    franchiseWeblocationId: number,
  ) {
    setOneClickedFranchiseWebLocationId(franchiseWeblocationId);
    setClickedFranchiseId(franchiseId);
  }

  async function searchNearbyLocations(
    searchQuery: string,
    brandDetails,
    isChangeNearByLocation?: boolean,
  ) {
    const constUsAPI = async () => {
      ContactUsApiCallHandler(formValues, formConfig, correlationID);
    };
    setIsLoading(true);
    const franchiseLimitValue = convertRange(franchiseLimit, units);
    const geoLocationResult = await fetchGeoLocation(formValues?.zipcode);
    if (geoLocationResult?.items && geoLocationResult?.items.length !== 0) {
      const position = geoLocationResult?.items[0]?.position;
      sessionStorage.setItem("distanceTo", JSON.stringify(position));
      const bbox = getBoundingBox(
        position?.lat,
        position?.lng,
        franchiseLimitValue,
      );
      const hBBox = new H.geo.Rect(bbox[0], bbox[1], bbox[2], bbox[3]);
      const franchiseLimitFunction =
        units === "km" ? milesToKilometers(franchiseLimit) : franchiseLimit;
      const response = formConfig?.useNearByLocationMilesEndpoint
        ? await WebLocationsMilesEndpoint(
            formConfig?.conceptCode,
            formValues,
            franchiseLimitFunction,
          )
        : await getAllLocations(formConfig?.conceptCode, formValues);

      setIsLoading(false);

      const result = filterData(response);
      const filteredData = formConfig?.useNearByLocationMilesEndpoint
        ? result
        : filterResults(hBBox, result, -1);

      if (filteredData?.length === 0) {
        setFoundNearByLocations(false);
        setIsLocationsModalOpen(false);
        if (
          brandDetails?.lead_contactapi === true &&
          !isChangeNearByLocation &&
          !state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]
        ) {
          constUsAPI();
        } else {
          setIsServiceModalOpen(true);
        }
      } else if (filteredData?.length === 1 && !isChangeNearByLocation) {
        setFoundNearByLocations(true);
        setIsLocationsModalOpen(false);
        const franchiseWebLocationId = filteredData[0]?.franchiseWebLocationId;
        const franchiseId = filteredData[0]?.franchiseId;
        if (formConfig?.conceptCode === conceptCodes.HMS_CONCEPT_CODE) {
          await handleHMSMultipleLocationPOS(
            franchiseWebLocationId,
            setCurrentStep,
            setIsLoading,
            formConfig?.conceptCode,
          );
          return filteredData;
        }
        if (formConfig.feeDetails?.displayInspectionFee)
          handleFeeInspection(franchiseWebLocationId);
        setFormValues((prevValues) => {
          const updatedValues = {
            ...prevValues,
            franchiseId: franchiseId,
            franchiseWebLocationId: franchiseWebLocationId,
            os_flow_50Mile_note: brandDetails?.os_flow_50Mile_note,
            conceptID: formConfig?.conceptid,
            dbaName: filteredData[0]?.doingBusinessAs,
          };
          if (typeof window?.localizationHelper === "function") {
            window?.localizationHelper(filteredData, true);
          }
          if (state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]) {
            if (formConfig?.conceptid === conceptIDs.MRA_CONCEPT_ID) {
              setValue(sharedStateKeys.IS_WARRANTY, true);
              if (
                formConfig?.steps[currentStep]?.steptype !==
                AppConstants.CONTACT_US_TYPE
              ) {
                setCurrentStep((prevStep) => prevStep + 1);
              } else {
                sessionStorageUtils.setItem(
                  "sAddressParam",
                  formValues?.zipcode as string,
                );
                //Booking call for MRA brand on zipcode page
                BookingApiCallHandler(
                  formConfig,
                  updatedValues,
                  setTechnicalModalOpen,
                );
              }
            } else {
              setCurrentStep((prevStep) => prevStep + 1);
            }
          } else {
            setOneLocationClicked(true);
            setSelectedNearByLocationData(franchiseId, franchiseWebLocationId);
          }
          return updatedValues;
        });
      } else if (
        filteredData?.length > 1 ||
        (filteredData.length === 1 && isChangeNearByLocation === true)
      ) {
        setFoundNearByLocations(true);
        setAllNearByLocations(filteredData);
        setnoOfNearByLocations(filteredData?.length);
        setIsLocationsModalOpen(true);
      }
      return filteredData;
    } else {
      if (
        brandDetails?.lead_contactapi === false ||
        isChangeNearByLocation === true
      ) {
        setIsServiceModalOpen(true);
      } else if (!state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]) {
        constUsAPI();
      } else {
        setIsServiceModalOpen(true);
      }
      console.log("Error: No items found in data.");
      return null;
    }
  }
  useEffect(() => {
    const handlePopState = () => {
      globalThis.history.back();
      return;
    };
    globalThis.addEventListener("popstate", handlePopState);
    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 0)
      globalThis.history.pushState(null, "", globalThis.location.href);
    else window.scrollTo({ top: 0, behavior: "smooth" });
    setFormValues((prevValues) => ({
      ...prevValues,
      currentStep: currentStep,
    }));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 1) {
      setIsModalOpen(false);
    }
  }, [formValues?.address]);

  useEffect(() => {
    if (suggestedSelected && makeAnotherCall) {
      handleNext();
    }
  }, [makeAnotherCall]);

  const sessionAndCorelation = getSessionAndCorrelationID();
  const correlationID = sessionAndCorelation.correlationID;

  const handleNext = async (
    wdgServiceNonPriciableSkuId = "",
    address = "",
    isLAPPricing = false,
    skipCaptureCall = false,
  ) => {
    // Prevent multiple simultaneous executions using ref-based click guard
    if (isHandlingNext.current) return;
    isHandlingNext.current = true;
    setHasMovedPastFirstStep(true);
    window.onbeforeunload = null;
    setIsLoading(true);
    if (!currentStepConfig) return;
    let canMoveToNextpage = true;
    if (
      currentStepConfig?.steptype === AppConstants.REQUEST_TIME_TYPE &&
      !enableCallBack
    ) {
      if (formConfig?.steps.length >= 3 && selectedScheduleId?.length === 0) {
        setMoveToNextPage(false);
        canMoveToNextpage = false;
      } else if (
        formConfig?.steps.length >= 3 &&
        selectedScheduleId?.length > 0
      ) {
        setMoveToNextPage(true);
      }
    }

    const isValid = updatedStepConfig?.elements?.every((element) => {
      if (element.type === AppConstants.AGNOSTIC_FORM) {
        const activeTabId = formValues.activeTabId as string;
        const filteredFields = (element.fields || []).filter(
          (field) => !isBusinessNameHidden(field.id, formValues),
        );
        return validateFields(
          filteredFields || [],
          formValues,
          setErrors,
          activeTabId,
          errors,
        );
      }
      if (element.type === "serviceSelector") {
        return validateServiceSelectionFields(element, formValues, setErrors);
      }
      if (element?.type === PROPERTY_AREA_CONFIRMATION) {
        return validateMapAreaSelectionFields(element, formValues, setErrors);
      }
      return true;
    });
    const isPricingPageValid = validatePricingFields(
      currentStepConfig?.steptype,
      state[sharedStateKeys.PRICING_CONFIG],
      formValues,
      setErrors,
    );
    if (
      isValid &&
      isPricingPageValid &&
      currentStep < totalSteps &&
      canMoveToNextStep(
        moveToNextPage,
        canMoveToNextpage,
        currentStepConfig?.steptype,
      )
    ) {
      const isOfferValid = await handleOfferValidation({
        currentStepConfig,
        formConfig,
        formValues,
        setFormValues,
        validateCESOffer,
      });

      if (!isOfferValid) {
        setIsLoading(false);
        isHandlingNext.current = false;
        return;
      }
      if (currentStep === 1) {
        setIsModalOpen(false);
      }
      let onlineSchedulingAvailable = false;
      let webLocationId = null;
      let franchiseId = null;
      let mlyEstimate = null;
      const isContactMe = sessionStorageUtils.getItem(IS_CONTACT_ME_BUTTON);
      if (currentStepConfig?.sequence === "1") {
        try {
          if (
            !formValues?.city &&
            !formValues?.state &&
            !formValues?.countryCode
          ) {
            const res = await fetchGeoLocation(formValues.zipcode);
            setFormValues((prevValues) => ({
              ...prevValues,
              city: res?.items[0]?.address?.city,
              state: res?.items[0]?.address?.state,
              countryCode: res?.items[0]?.address?.countryCode,
            }));
          } else {
            setFormValues((prevValues) => ({
              ...prevValues,
              vendorName: formConfig.vendorName,
              conceptCode: formConfig.conceptCode,
            }));
          }
          let addressPayload = formValues?.zipcode;
          if (makeAnotherCall) {
            addressPayload = `${enteredAddressWithoutZipcode} ${formValues?.address2 ?? ""} ${formValues?.zipcode}`;
          }
          const suggestedAddress1 = sessionStorageUtils.getItem(
            SUGGEST_ADDRESS_VALUE,
          );
          if (suggestedAddress1) {
            const addressVal =
              suggestedAddress1 != noAddressMsg ? suggestedAddress1 : "";
            const {
              address2 = "",
              city = "",
              state = "",
              zipcode = "",
            } = formValues;
            addressPayload =
              `${addressVal} ${address2} ${city} ${state} ${zipcode}`.trim();
            sessionStorageUtils.removeItem(SUGGEST_ADDRESS_VALUE);
          }
          let res = await fetchFranchiseLookupByServiceAddress(
            addressPayload as string,
            formConfig?.conceptid,
            formValues?.userInfoServiceId as string,
          );
          const isNearByLocation =
            oneLocationClicked ||
            sessionStorageUtils.getItem("isNearbyRedirect") === "true";

          if (isNearByLocation) {
            res = [
              {
                franchiseWebLocationId: oneClickedFranchiseWebLocationId,
                franchiseId: clickedFranchiseId,
              },
            ];
          }

          if (formValues?.zipcode) {
            sessionStorage.setItem("sAddressParam", formValues.zipcode);
          }
          if (res?.message === MULTIPLE_LOCATIONS_ERROR_MSG) {
            const {
              address = "",
              address1 = "",
              address2 = "",
              city = "",
              state = "",
              zipcode = "",
            } = formValues;
            if (address && zipcode) {
              let addressVal = address1 || address;
              addressVal = addressVal != noAddressMsg ? addressVal : "";
              const fullAddress =
                `${addressVal} ${address2 ?? ""} ${city} ${state} ${zipcode}`.trim();
              res = await fetchFranchiseLookupByServiceAddress(
                fullAddress,
                formConfig?.conceptid,
                formValues?.userInfoServiceId as string,
              );
            } else {
              setFormValues((prevValues) => ({
                ...prevValues,
                address: "",
                address2: "",
              }));
              setAddressBumperOpen(true);
              return;
            }
          }
          if (res.err) {
            setIsServiceModalOpen(true);
            setAddressBumperOpen(false);
            return;
          }
          handleOfferDetails(
            formConfig,
            formValues,
            setFormValues,
            setOfferRemoved,
          );
          if (res && res.length > 0) {
            const storedFranchiseWebLocationId = JSON.parse(
              localStorage.getItem("franchiseWebLocationId"),
            );
            if (
              selectedOffer &&
              storedFranchiseWebLocationId &&
              storedFranchiseWebLocationId !== res[0]?.franchiseWebLocationId
            ) {
              const { shouldRemoveOffer } = handleOfferRemoval(formConfig);
              if (shouldRemoveOffer) {
                sessionStorageUtils.removeItem("offerDetails");
                setFormValues((prevValues) => ({
                  ...prevValues,
                  offerRemoved: true,
                }));
                setOfferRemoved(true);
              }
            }
            const [isOnlineScheduling, estimateRes] =
              await handleFranchiseLookup(res, brandDetails, isNearByLocation);
            setOnline(isOnlineScheduling);
            webLocationId = res[0]?.franchiseWebLocationId;
            franchiseId = res[0]?.franchiseId;
            onlineSchedulingAvailable = isOnlineScheduling;
            mlyEstimate = estimateRes;
            removeValue(sharedStateKeys.IS_WARRANTY);
            let updatedFormValues;
            setFormValues((prevValues) => {
              const updatedValue = {
                ...prevValues,
                contactMe: true,
              };
              updatedFormValues = updatedValue;
              return updatedValue;
            });

            if (formConfig?.conceptCode === conceptCodes.HMS_CONCEPT_CODE) {
              try {
                const posLookupResArr = await fetchFranchisePosLookup(
                  webLocationId as string,
                );
                const locationWebsiteUrl = res[0]?.locationWebsiteUrl;
                await handleISNRedirect(
                  posLookupResArr,
                  onlineSchedulingAvailable,
                  setCurrentStep,
                  locationWebsiteUrl,
                  formConfig.conceptCode,
                  setIsLoading,
                );
              } catch (error) {
                console.error("Error fetching POS Lookup:", error);
              }
            }
            const skipCalendar =
              formConfig?.conceptid === conceptIDs.MLY_CONCEPT_ID &&
              skipMLYCalendar(updatedFormValues);
            const isNearBy =
              oneLocationClicked ||
              sessionStorageUtils.getItem("isNearbyRedirect") === "true";
            if (isContactMe === "true" || skipCalendar || isNearBy) {
              window.onbeforeunload = null;
              if (
                suggestedSelected !== "true" &&
                (suggestedAddress?.includes(formValues?.zipcode as string) ||
                  hasNoZipCodes)
              ) {
                const apiResults =
                  state[sharedStateKeys.S_STATE_ADDRESS_SUGGESTION_RES];
                if (apiResults?.length > 0 && !isModalOpen) {
                  setIsModalOpen(true);
                  return;
                }
              }
              sessionStorageUtils.removeItem(IS_CONTACT_ME_BUTTON);
              //For Near by location , MLY Non calendar flow and LAP contact me flow
              await BookingApiCallHandler(
                formConfig,
                updatedFormValues,
                setTechnicalModalOpen,
                state,
                false,
                {
                  bookingWrapper: bookingWrapperEnabled,
                  current: currentStep,
                  total: currentStep + 1,
                  upSellEnabled: upSellEnabled,
                },
              );
              return;
            }

            if (
              formConfig?.steps[currentStep + 1]?.steptype ===
              AppConstants.SERVICE_BOT_SECTION
            ) {
              try {
                const jsonData = await fetch(apiConfig?.fidJsonURL);
                const jsonResponse = await jsonData.json();
                const foundLocation =
                  jsonResponse.franchiseConfigurations?.[webLocationId];
                let updatedFormValue;
                setFormValues((prevValues) => {
                  const updatedValue = {
                    ...prevValues,
                    ...foundLocation,
                  };
                  updatedFormValue = updatedValue;
                  return updatedValue;
                });
                if (updatedFormValue?.leadSourceName) {
                  const label = serviceDetailsLabels.LEAD_SOURCE_TEXT;
                  const leadSourceText = `${label}: ${updatedFormValue.leadSourceName}`;
                  sessionStorage.setItem("serviceDescription", leadSourceText);
                }
                if (!foundLocation) {
                  //Bot flow
                  await BookingApiCallHandler(
                    formConfig,
                    updatedFormValue,
                    setTechnicalModalOpen,
                  );
                  setErrorPricingModalOpen(true);
                  return;
                }
              } catch (error) {
                console.error("Error loading ServiceBot configuration:", error);
                let updatedFormValue;
                setFormValues((prevValues) => {
                  const updatedValue = {
                    ...prevValues,
                  };
                  updatedFormValue = updatedValue;
                  return updatedValue;
                });
                // Bot flow
                await BookingApiCallHandler(
                  formConfig,
                  updatedFormValue,
                  setTechnicalModalOpen,
                );
                setErrorPricingModalOpen(true);
                return;
              }
            }
          }
          if (res?.length === 0) {
            if (
              isContactMe ||
              formConfig?.steps[currentStep + 1]?.steptype ===
                AppConstants.SERVICE_BOT_SECTION
            ) {
              if (
                brandDetails?.lead_contactapi === true &&
                !state[sharedStateKeys.S_STATE_CHANGE_NEAR_BY_LOCATION]
              ) {
                await ContactUsApiCallHandler(
                  formValues,
                  formConfig,
                  correlationID,
                );
              } else {
                setIsServiceModalOpen(true);
              }
              return;
            }
            handleNonServicableZipcodes();
            return;
          }
          if (
            suggestedSelected !== "true" &&
            (suggestedAddress?.includes(formValues?.zipcode as string) ||
              hasNoZipCodes)
          ) {
            if (res?.length === 0) setNotServicable(true);
            const apiResults =
              state[sharedStateKeys.S_STATE_ADDRESS_SUGGESTION_RES];
            if (
              apiResults?.length > 0 &&
              !isModalOpen &&
              currentStepConfig?.steptype === AppConstants.CONTACT_US_TYPE
            ) {
              setIsModalOpen(true);
              return;
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          setIsLoading(false);
          if (error?.status === 400) {
            setMakeAnotherCall(true);
            if (!suggestedSelected) {
              setIsModalOpen(true);
            }
          } else {
            setMakeAnotherCall(false);
            setIsModalOpen(false);
            setTechnicalModalOpen(true);
          }
          console.error("Error fetching franchise lookup:", error);
          return;
        } finally {
          setIsLoading(false);
          isHandlingNext.current = false;
        }
        isHandlingNext.current = true;
        const nextStep = formConfig?.steps[currentStep + 1];
        let updatedFormValue;
        setFormValues((prevValues) => {
          let updatedValues = {
            ...prevValues,
          };
          updatedValues = maybeAddWdgNonPriciableSkuId(
            updatedValues,
            wdgServiceNonPriciableSkuId,
          );
          updatedFormValue = updatedValues;
          return updatedValues;
        });
        const triggerUPSCall =
          (nextButtonClickedEnable && !bookingWrapperEnabled) || isLAPPricing;
        if (triggerUPSCall) {
          const result = await pauseLeadTriggerBookingAPI(
            nextStep,
            updatedFormValue,
            state,
            formConfig,
            setTechnicalModalOpen,
            makePauseLeadBookingCall,
          );
          if (result && !result?.customerId && formConfig.conceptid === "29") {
            setErrorPricingModalOpen(true);
            return;
          } else if (result?.customerId && formConfig.conceptid === "29") {
            setFormValues((prevValues) => ({
              ...prevValues,
              customerNumber: result?.customerId,
            }));
            if (
              result?.responseMessage === CustomerRecordUpdatedText ||
              result?.responseMessage === CustomerSuccessfullyAddedText
            ) {
              isHandlingNext.current = false;
              setCurrentStep((prevStep) => prevStep + 1);
              return;
            } else {
              globalThis.location.replace(formConfig?.redirectionPageUrl);
            }
          }
        }
      }
      if (
        (online || onlineSchedulingAvailable) &&
        (formConfig?.steps[currentStep + 1]?.steptype ===
          AppConstants.REQUEST_TIME_TYPE ||
          isModifierFlowCalendarRequired(
            formConfig,
            state[sharedStateKeys.MODIFIER_FLOW_ID],
          ))
      ) {
        const techavailablityRes = await scheduleTech(
          webLocationId,
          wdgServiceNonPriciableSkuId,
        );

        if (!techavailablityRes || techavailablityRes?.length === 0) {
          if (
            formConfig?.steps[totalSteps - 1].steptype ===
            AppConstants.PAYMENT_IFRAME_SECTION
          ) {
            handleSubmit();
            return;
          } else if (
            formConfig?.steps[totalSteps - 1].steptype ===
            AppConstants.REQUEST_TIME_TYPE
          ) {
            const updatedValues = formValues;
            if (!updatedValues.franchiseWebLocationId) {
              updatedValues.franchiseWebLocationId = webLocationId;
              updatedValues.franchiseId = franchiseId;
            }
            totalSteps--;
            handleSubmit(updatedValues);
            return;
          } else {
            setCurrentStep((prevStep) => prevStep + 2);
            return;
          }
        }
      }

      const isJUKBrand = () => {
        if (formConfig?.conceptid === conceptIDs.JUK_CONCEPT_ID)
          return (
            formConfig.steps?.[currentStep + 1]?.steptype !==
            AppConstants.PAYMENT_IFRAME_SECTION
          );
        return true;
      };

      if (
        !skipCaptureCall &&
        !isLAPPricing &&
        bookingWrapperEnabled &&
        formConfig?.conceptid !== conceptIDs.MRA_CONCEPT_ID &&
        isJUKBrand()
      ) {
        const updatedValues = formValues;
        if (!updatedValues.franchiseWebLocationId) {
          updatedValues.franchiseWebLocationId = webLocationId;
          updatedValues.franchiseId = franchiseId;
        }
        if (address) updatedValues.address1 = address;
        if (mlyEstimate) updatedValues.mlyEstimate = mlyEstimate;
        BookingApiCallHandler(
          formConfig,
          updatedValues,
          setTechnicalModalOpen,
          {},
          false,
          {
            bookingWrapper: bookingWrapperEnabled,
            current: currentStep,
            total: totalSteps,
            upSellEnabled: upSellEnabled,
          },
        );
      }

      // JUK Payment Scenario
      await handleJukPaymentScenario({
        formValues,
        formConfig,
        setTechnicalModalOpen,
        setFormValues,
        bookingWrapper: bookingWrapperEnabled,
      });

      setTimeout(() => {
        setCurrentStep((prevStep) => prevStep + 1);
        isHandlingNext.current = false;
      }, 400);
    } else {
      setIsLoading(false);
      isHandlingNext.current = false;
    }
    setIsLoading(false);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      if (
        formConfig?.steps[currentStep - 1]?.steptype ===
        AppConstants.BOOK_AN_INSPECTION
      ) {
        removeValue(sharedStateKeys.IS_WARRANTY);
      }
      if (
        // if previous screen is calendar
        formConfig?.steps[currentStep - 1]?.steptype ===
          AppConstants.REQUEST_TIME_TYPE &&
        state[sharedStateKeys.BOOK_AN_INSPECTION_ENABLE]
      ) {
        if (online) {
          if (
            state[sharedStateKeys.IS_WARRANTY] ||
            formValues.manufacturerWarranty === "yes"
          ) {
            setCurrentStep((prevStep) => prevStep - 2);
          } else {
            setCurrentStep((prevStep) => prevStep - 1);
          }
        } else {
          setCurrentStep((prevStep) => prevStep - 2);
        }
      } else {
        if (
          state[sharedStateKeys.IS_TECH_AVAILABLE_FAILS] &&
          formConfig?.steps[currentStep - 1]?.steptype ===
            AppConstants.REQUEST_TIME_TYPE
        ) {
          setCurrentStep((prevStep) => prevStep - 2);
          isHandlingNext.current = false;
          return;
        }
        if (state[sharedStateKeys.IS_SERVICE_PAGE_SKIPPED]) {
          setCurrentStep((prevStep) => prevStep - 2);
          isHandlingNext.current = false;
          return;
        }
        setCurrentStep((prevStep) => prevStep - 1);
      }
    }
    isHandlingNext.current = false;
  };

  const technicalErrorModalImage = findPropertyById(formConfig, "moj-logo");
  const techincalErrorModalText = findPropertyById(
    formConfig,
    "technical-error-message",
  );

  const errorModalImage = findPropertyById(formConfig, "moj-logo");
  const localisedLogo = localisedLogoSelect ?? errorModalImage;

  const errorModalText = findPropertyById(formConfig, "service-error-message");
  let totalSteps = formConfig ? formConfig.steps.length : 0;

  sessionStorage.setItem("conceptCode", formConfig?.conceptCode);

  const handleSkipAndSubmit = async (skipCalendar = false) => {
    window.onbeforeunload = null;
    const updatedFormValues = { ...formValues };

    const updatedSharedState = { ...state };
    if (nonPricablePressureWashingDataRef?.current?.length > 0) {
      updatedSharedState[sharedStateKeys.PRICE_QUOTE_ESTIMATION_OBJECT] =
        nonPricablePressureWashingDataRef?.current;
    }

    if (nonPricableQuoteObjRef?.current?.length > 0) {
      updatedSharedState[sharedStateKeys.NON_PRICABLE_QUOTE_ESTIMATION_OBJECT] =
        nonPricableQuoteObjRef?.current;
    }

    if (
      formConfig?.steps[currentStep]?.steptype ==
      state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE]
    ) {
      updatedFormValues.priceQuoteEstimateAvailable = false;
    }

    //When we click "Skip" link from calendar screen, we clear the scheduleId
    if (
      currentStepConfig?.steptype === AppConstants.REQUEST_TIME_TYPE ||
      (currentStepConfig?.steptype === stepTypes.STEP_PRICE_QUOTE_ESTIMATION &&
        formValues?.[AppConstants.FIELD_ID_MULTI_SERVICE_CATEGORY]?.length ===
          0)
    ) {
      updatedFormValues.scheduleId = "";
      updatedFormValues.date = "";
      updatedFormValues.slot = "";
    }
    //Booking call for all brands in case of skip and submit
    BookingApiCallHandler(
      formConfig,
      updatedFormValues,
      setTechnicalModalOpen,
      updatedSharedState,
      false,
      {
        bookingWrapper: bookingWrapperEnabled,
        current: currentStep,
        total:
          skipCalendar || bookingWrapperEnabled ? currentStep + 1 : totalSteps,
        upSellEnabled: upSellEnabled,
      },
    );
  };

  const zipcodeModalData = {
    logo: localisedLogoSelect ?? findPropertyById(formConfig, "brand-logo"),
    text: findPropertyById(formConfig, "zipcode-change-prompt"),
    okLabel: findPropertyById(formConfig, "zipcode-button-ok-text"),
    cancelLabel: findPropertyById(formConfig, "zipcode-button-cancel-text"),
  };

  const getNewAddressFromZipcode = async () => {
    removeValue(sharedStateKeys.IS_WARRANTY);
    setIsLoading(true);
    setFormValues((prevValues) => ({
      ...prevValues,
      scheduleId: "",
      technicianIds: "",
      date: "",
      slot: "",
    }));
    setSelectedScheduleId("");
    setIsZipcodeChangeModalOpen(false);
    try {
      if (!formValues?.city && !formValues?.state && !formValues?.countryCode) {
        const res = await fetchGeoLocation(formValues.zipcode);
        setFormValues((prevValues) => ({
          ...prevValues,
          city: res?.items[0]?.address?.city,
          state: res?.items[0]?.address?.state,
          countryCode: res?.items[0]?.address?.countryCode,
        }));
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          vendorName: formConfig.vendorName,
          conceptCode: formConfig.conceptCode,
        }));
      }
      let addressPayload = formValues?.zipcode;
      if (makeAnotherCall) {
        addressPayload = `${enteredAddressWithoutZipcode} ${formValues?.address2 ?? ""} ${formValues?.zipcode}`;
      }
      const res = await fetchFranchiseLookupByServiceAddress(
        addressPayload as string,
        formConfig?.conceptid,
        formValues?.userInfoServiceId as string,
      );
      if (res?.message === MULTIPLE_LOCATIONS_ERROR_MSG) {
        setAddressBumperOpen(true);
        return;
      }
      if (res && res.length > 0) {
        const [isOnlineScheduling] = await handleFranchiseLookup(
          res,
          brandDetails,
        );
        setOnline(isOnlineScheduling);
      }
      if (res && res.length === 0) {
        handleNonServicableZipcodes();
        return;
      } else if (
        res?.length !== 0 &&
        suggestedSelected !== "true" &&
        (suggestedAddress?.includes(formValues?.zipcode as string) ||
          hasNoZipCodes)
      ) {
        const apiResults =
          state[sharedStateKeys.S_STATE_ADDRESS_SUGGESTION_RES];
        if (apiResults?.length > 0 && !isModalOpen) {
          setIsModalOpen(true);
          return;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false);
      if (error?.status === 400) {
        setMakeAnotherCall(true);
        if (!suggestedSelected) {
          setIsModalOpen(true);
        }
      } else {
        setMakeAnotherCall(false);
        setIsModalOpen(false);
        setTechnicalModalOpen(true);
      }
      console.error("Error fetching franchise lookup:", error);
      return;
    } finally {
      setIsLoading(false);
    }
    if (formValues?.zipcode) {
      sessionStorage.setItem("sAddressParam", formValues.zipcode as string);
    }
    setCurrentStep((prevStep) => prevStep - 2);
  };
  const handleSubmit = async (newFormValues?: FormValues) => {
    const jukSharedState =
      formConfig?.conceptid === conceptIDs.JUK_CONCEPT_ID
        ? { [sharedStateKeys.IS_PAUSE_LEAD]: false }
        : {};
    if (
      formConfig?.steps[currentStep]?.steptype === stepTypes.PAYMENT_IFRAME &&
      iframeLoadedWithoutErrors
    ) {
      const targetOrigin = extractDomain(
        formConfig?.steps[currentStep]?.paymentUrl,
      );
      const success = initiatePayment(formValues.jkcode, targetOrigin);
      if (!success) {
        setTechnicalModalOpen(true);
      }
      return;
    }

    if (
      formConfig?.steps[currentStep]?.steptype ===
        AppConstants.PAYMENT_IFRAME_SECTION &&
      (formConfig?.conceptid === conceptIDs.LAP_CONCEPT_ID ||
        formConfig?.conceptid === conceptIDs.JUK_CONCEPT_ID) &&
      !iframeLoadedWithoutErrors
    ) {
      globalThis.location.replace(formConfig?.redirectionPageUrl);
      return;
    }

    const isValid = validateAllSteps(
      updatedStepConfig,
      formValues,
      setErrors,
      errors,
    );

    const isPricingPageValid = validatePricingFields(
      currentStepConfig?.steptype,
      state[sharedStateKeys.PRICING_CONFIG],
      formValues,
      setErrors,
    );

    if (!isValid || !isPricingPageValid) return;

    if (
      formConfig.conceptid === conceptIDs.LAP_CONCEPT_ID &&
      formConfig?.steps[currentStep]?.steptype === stepTypes.HOW_WE_CAN_REACH
    ) {
      const result = await postQuote();
      const redirectionUrl = formConfig?.redirectionPageUrl ?? "/confirmation";
      if (result && !result.error && redirectionUrl) {
        window.onbeforeunload = null;
        const redirectUrl = isInboundFromNbly()
          ? `${redirectionUrl}?leadconcept=${formConfig.conceptCode}`
          : redirectionUrl;
        globalThis.location.replace(redirectUrl);
      }
      return;
    }

    if (formValues?.zipcode !== sessionStorage.getItem("sAddressParam")) {
      setIsZipcodeChangeModalOpen(true);
      isHandlingNext.current = false;
    } else {
      await PresignedUrlUploader({
        setIsLoading,
        images: state[sharedStateKeys.SET_UPLOADED_IMAGES],
      });
      if (
        currentStepConfig?.steptype === AppConstants.CONTACT_US_TYPE &&
        formConfig?.steps?.length === 1
      ) {
        await handleContactUsSingleStep({
          formValues,
          formConfig,
          setIsLoading,
          setFormValues,
          handleNonServicableZipcodes,
          setMakeAnotherCall,
          setIsModalOpen,
          setTechnicalModalOpen,
        });
      }
      if (
        currentStepConfig?.steptype === AppConstants.REQUEST_TIME_TYPE &&
        !enableCallBack
      ) {
        if ((formValues?.scheduleId as string)?.length !== 0) {
          setMoveToNextPage(true);
          BookingApiCallHandler(
            formConfig,
            formValues,
            setTechnicalModalOpen,
            { ...state, ...jukSharedState },
            false,
            {
              bookingWrapper: bookingWrapperEnabled,
              current: currentStep,
              total: totalSteps,
              upSellEnabled: upSellEnabled,
            },
          );
        } else {
          setMoveToNextPage(false);
        }
      } else if (
        currentStepConfig.id === AppConstants.CONTACT_US_ID &&
        currentStepConfig.sequence !== "1"
      ) {
        //this condition is for MRA brand
        const mraBookingCall = () => {
          BookingApiCallHandler(
            formConfig,
            formValues,
            setTechnicalModalOpen,
            state,
            false,
            {
              bookingWrapper: bookingWrapperEnabled,
              current: currentStep,
              total: totalSteps,
              upSellEnabled: upSellEnabled,
            },
          );
        };
        if (
          suggestedSelected !== "true" &&
          (suggestedAddress?.includes(formValues?.zipcode as string) ||
            hasNoZipCodes)
        ) {
          const apiResults =
            state[sharedStateKeys.S_STATE_ADDRESS_SUGGESTION_RES];
          if (apiResults?.length > 0 && !isModalOpen) {
            setIsModalOpen(true);
            return;
          } else {
            mraBookingCall();
          }
        } else {
          mraBookingCall();
        }
      } else {
        if (
          currentStepConfig.steptype === stepTypes.STEP_PRICE_QUOTE_REQUEST &&
          formConfig?.conceptid === conceptIDs.WDG_CONCEPT_ID &&
          (formValues?.proptype === PROTYPE_COMMERCIAL_TEXT ||
            formValues?.pricequoteselection === "no")
        ) {
          totalSteps -= getStepsToRemove(formConfig);
        }
        let updatedFormValues = formValues;
        if (newFormValues) updatedFormValues = newFormValues;
        BookingApiCallHandler(
          formConfig,
          updatedFormValues,
          setTechnicalModalOpen,
          state,
          false,
          {
            bookingWrapper: bookingWrapperEnabled,
            current: currentStep,
            total: totalSteps,
            upSellEnabled: upSellEnabled,
          },
        );
      }
    }
  };

  const conceptCode = sessionStorage.getItem("conceptCode");

  const FormActionButtonsComponent = () => (
    <span className="form-action-buttons">
      <FormActionButtons
        currentStep={currentStep}
        totalUpdatedSteps={totalUpdatedSteps}
        handleNext={handleNext}
        handleSubmit={handleSubmit}
        handleSkipAndSubmit={handleSkipAndSubmit}
        getTotalUpdatedSteps={() => totalSteps - 1}
        online={online}
        formConfig={formConfig}
        handleBack={handleBack}
        enableCallBack={enableCallBack}
        currentStepType={currentStepConfig?.steptype}
      />
    </span>
  );

  if (!formConfig) {
    //This is only for localhost
    return <Loader isLoading={true} />;
  }

  return (
    <section
      style={{
        backgroundColor: theme.palette.background.default,
        backgroundImage: "url(/brand/_assets/images/bg-pattern-full-2.svg)",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
      }}
      className={`scheduler-steps scheduler-steps-${formConfig?.conceptCode} current-step-${currentStepConfig?.steptype}`}
    >
      <Typography
        variant="h1"
        id="scheduler-heading"
        gutterBottom
        className={`scheduler-heading`}
      >
        {formConfig.schedulerHeading}
      </Typography>
      <Container
        className={`form-step form-step-${formConfig?.conceptCode}`}
        id="landingPageContainer"
        style={{ position: "relative" }}
      >
        {isGlobalSkeletonLoading && enabledConcepts.includes(conceptCode) && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1000,
              backgroundColor: "rgb(255, 255, 255)",
              pointerEvents: "none",
            }}
          >
            <SkeletonLoader layout={SKELETON_LOADER_LAYOUT_PAGE} />
          </div>
        )}
        {formConfig.iconTiles?.child && (
          <>
            {formConfig.iconTiles.child?.length !== 0
              ? sessionStorageUtils.setItem(
                  SESSION_TEXT_TILES,
                  formConfig.iconTiles?.child,
                )
              : sessionStorageUtils.removeItem(SESSION_TEXT_TILES)}
            {formConfig.iconTiles?.tilesEnabled && (
              <TextIconTiles
                child={formConfig.iconTiles.child}
                hideMobile={true}
              />
            )}
          </>
        )}
        <ProgressBar
          currentStep={currentStep}
          totalSteps={formConfig?.steps?.length}
        />
        {(selectedOffer || offerRemoved) &&
          currentStepConfig?.offersSection?.displaySpecialOffer &&
          formConfig?.offerPosition !== "bottom" && (
            <OffersPage
              offerDetails={selectedOffer || {}}
              offerRemoved={offerRemoved}
            />
          )}
        <StepTitle currentStep={currentStepConfig} />
        <form className={`${currentStepConfig?.steptype}`} id="form">
          <div className={`form-step-${currentStepConfig?.steptype}`}>
            <SchedulerStepHandler
              currentStepConfig={currentStepConfig}
              errors={errors}
              setErrors={setErrors}
              scheduleCollection={scheduleCollection}
              setSelectedScheduleId={setSelectedScheduleId}
              setSelectedDate={setSelectedDate}
              setSelectedSlot={setSelectedSlot}
              setCalendarTimeSlot={setCalendarTimeSlot}
              setInitialDate={setInitialDate}
              moveToNextPage={moveToNextPage}
              enableCallBack={enableCallBack}
              setEnableCallBack={setEnableCallBack}
              setTechnicalModal={setTechnicalModalOpen}
              handleBack={handleBack}
              handleNext={handleNext}
              handleSkip={handleSkipAndSubmit}
              nonPricablePressureWashingDataRef={
                nonPricablePressureWashingDataRef
              }
              nonPricableQuoteObjRef={nonPricableQuoteObjRef}
              enable_new_calendar_design={Boolean(
                brandDetails.enable_new_calendar_design,
              )}
              offerDetails={selectedOffer}
              formActionButtons={<FormActionButtonsComponent />}
            />
          </div>
          {!isMobile && (
            <>
              <div
                style={getBannerPosition(
                  currentStepConfig?.offerApply?.showBannerOfferOnTop,
                )}
              >
                {formConfig?.offerPosition === "bottom" &&
                  currentStepConfig?.offersSection?.displaySpecialOffer && (
                    <OfferDetailsFooter
                      offerDetails={selectedOffer}
                      offerRemoved={offerRemoved}
                    />
                  )}
                {currentStepConfig?.offerApply &&
                  !landingPageOfferSourceValidation() && (
                    <OffersApply
                      field={currentStepConfig?.offerApply}
                      setErrors={setErrors}
                    />
                  )}
              </div>
              <FormActionButtonsComponent />
            </>
          )}
          <AddressBumper
            Open={isModalOpen}
            onClose={handleCloseModal}
            enteredAddress={enteredAddress}
            suggestedAddress={suggestedAddress}
            setCurrentStep={setCurrentStep}
            zipcode={formValues?.zipcode}
            optionalAddress={formValues?.address2}
            enteredAddressWithoutZipcode={enteredAddressWithoutZipcode}
            handleFunction={handleNext}
            setTechnicalModalOpen={setTechnicalModalOpen}
            currentStep={currentStep}
            bookingWrapper={brandDetails.capture_lead_flag as boolean}
            upSellEnabled={upSellEnabled}
            {...(notServicable && {
              handleNonService: handleNonServicableZipcodes,
            })}
          />
          <ErrorModal
            key={isServiceModalOpen ? "open" : "closed"}
            openModal={isServiceModalOpen}
            onClose={handleCloseServiceModal}
            errorModalImage={localisedLogo}
            errorModalText={errorModalText}
            zipcode={formValues?.zipcode as string}
            formConfig={formConfig}
          />
          <ZipcodeChangeModal
            key={isZipcodeChangeModalOpen ? "open" : "closed"}
            openModal={isZipcodeChangeModalOpen}
            onConfirmation={getNewAddressFromZipcode}
            onClose={handleCloseZipcodeChangeModal}
            modalData={zipcodeModalData}
            formConfig={formConfig}
          />

          <LocationsModal
            key={isLocationsModalOpen ? "modalOpen" : "modalClosed"}
            openModal={isLocationsModalOpen}
            onClose={handleCloseLocationsModal}
            zipcode={formValues?.zipcode as string}
            noOfNearByLocations={noOfNearByLocations}
            locationModalLogo={localisedLogo}
            allNearByLocations={allNearByLocations}
            onLocationClick={handleLocationClick}
          />

          <TechnicalErrorModal
            key={
              technicalModalOpen ? "technicalmodalopen" : "technicalmodalclosed"
            }
            openModal={technicalModalOpen}
            onClose={handleCloseTechincalModal}
            errorModalImage={localisedLogoSelect ?? technicalErrorModalImage}
            errorModalText={techincalErrorModalText}
            formConfig={formConfig}
          />
          <AddressBumperFormModal
            errorModals={formConfig?.errors[0]}
            addressBumperOpen={addressBumperOpen}
            setAddressBumperOpen={setAddressBumperOpen}
            errors={errors}
            setErrors={setErrors}
            setOnline={setOnline}
            brandDetails={brandDetails}
            setCurrentStep={setCurrentStep}
            setServiceModalOpen={setIsServiceModalOpen}
            localisedImgLogo={localisedLogoSelect}
          />
          <ErrorPricingModal
            openModal={errorPricingModalOpen}
            errorPriceModal={formConfig?.errors[0]}
            formConfig={formConfig}
          />

          {currentStepConfig?.elements?.map(
            ({ contractcanceltext }, index) =>
              contractcanceltext && (
                <p className="contract-cancel-text" key={index}>
                  {contractcanceltext}
                </p>
              ),
          )}
        </form>
      </Container>
      <Loader isLoading={isLoading} />
    </section>
  );
};

export default SchedulerSteps;
